/**
 * **The offline day** (the booth, 7g). What a station keeps so the desk works with the cable
 * pulled: the day's roster and the tags printed today, read at Open the booth and every few
 * minutes after; and the queue — every check-in, check-out, undo, reassign, scan and tag print
 * made with no network, in order, each with the real time it happened. On reconnect the queue is
 * sealed in that order (`/volunteers/booth/sync`); what the server cannot seal lands on NEEDS
 * WORK, never dropped.
 *
 * Kept in this browser's storage, which is the station's own — the station is one enrolled
 * device. Every read and write survives storage being refused: the desk then works online only.
 */

export interface DayPerson {
  personId: string;
  name: string;
  quiet: boolean;
  household?: string | null;
  needsWaiver: boolean;
  today: {
    shiftId: string;
    signupId: string | null;
    area: string;
    title: string;
    startsAt: string;
    endsAt: string;
    state: 'expected' | 'in' | 'out';
    workedBy: string | null;
  }[];
  otherDays: { day: string; area: string; startsAt: string; endsAt: string }[];
  issue: { eligible: boolean; out: { id: string; itemName: string; number: string }[] };
}

export interface DayCache {
  day: string;
  people: DayPerson[];
  tags: { code: string; personId: string; shiftId: string; inAt: string | null }[];
  at: string;
}

export interface QueuedItem {
  id: string;
  kind: 'check-in' | 'check-out' | 'undo' | 'reassign' | 'scan' | 'reprint';
  at: string;
  personId?: string | null;
  shiftId?: string | null;
  signupId?: string | null;
  walkUp?: boolean;
  proceed?: { name?: string; reason: string } | null;
  toShiftId?: string | null;
  name?: string | null;
  reason?: string | null;
  code?: string | null;
  tag?: string | null;
}

const DAY_KEY = 'cider-booth-day';
const QUEUE_KEY = 'cider-booth-queue';
const SINCE_KEY = 'cider-booth-offline-since';

function read(key: string): unknown {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}
function write(key: string, value: unknown): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage refused: the desk works online only, and says so by having no queue.
  }
}

export const loadDay = (): DayCache | null => read(DAY_KEY) as DayCache | null;
export const saveDay = (d: DayCache): void => {
  write(DAY_KEY, d);
};
export const loadQueue = (): QueuedItem[] => (read(QUEUE_KEY) as QueuedItem[] | null) ?? [];
export const saveQueue = (q: QueuedItem[]): void => {
  write(QUEUE_KEY, q.length ? q : null);
};
export const loadSince = (): string | null => read(SINCE_KEY) as string | null;
export const saveSince = (at: string | null): void => {
  write(SINCE_KEY, at);
};

/** A tag code made here when the server cannot be reached — the same alphabet the server uses. */
export function newTagCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => alphabet[b % alphabet.length]).join('');
}

/** The day's people whose name holds every word typed — the find, with no network. */
export function findInDay(day: DayCache | null, q: string): DayPerson[] {
  if (!day) return [];
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  return day.people.filter((p) => words.every((w) => p.name.toLowerCase().includes(w))).slice(0, 6);
}

/** "4 waiting to sync · offline since 9:41" — the one sentence about it on the operator screen. */
export function queueSays(n: number, since: string | null, time: (at: string) => string): string {
  const waiting = n ? `${String(n)} waiting to sync` : 'Nothing waiting to sync';
  return since ? `${waiting} · offline since ${time(since)}` : waiting;
}
